import { useEffect, useState } from 'react';
import { 
  Ticket, Plus, Search, Filter, Copy, Trash2, Edit, 
  Calendar, Clock, CheckCircle, XCircle, RotateCcw,
  Percent, DollarSign, Truck, Tag
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUPON_TYPES = [
  { value: 'percentage', label: 'Percentage Off', icon: Percent },
  { value: 'flat', label: 'Flat Discount', icon: DollarSign },
  { value: 'free_shipping', label: 'Free Shipping', icon: Truck }
];

export const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    value: 10,
    min_order_amount: 0,
    max_discount_amount: '',
    usage_limit: '',
    per_user_limit: 1,
    valid_from: '',
    valid_until: '',
    applies_to: 'all',
    category_ids: [],
    product_ids: [],
    is_active: true,
    description: ''
  });

  useEffect(() => {
    loadCoupons();
    loadCategoriesAndProducts();
  }, [statusFilter]);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/admin/coupons${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data.coupons || []);
    } catch (error) {
      console.error('Error loading coupons:', error);
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const loadCategoriesAndProducts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCategories(catRes.data.categories || []);
      setProducts(prodRes.data.products || []);
    } catch (error) {
      console.error('Error loading categories/products:', error);
    }
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'JAT';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    if (!formData.valid_from || !formData.valid_until) {
      toast.error('Please set validity dates');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_until: new Date(formData.valid_until).toISOString()
      };

      if (editingCoupon) {
        await axios.put(`${API}/admin/coupons/${editingCoupon.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coupon updated successfully');
      } else {
        await axios.post(`${API}/admin/coupons`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coupon created successfully');
      }

      setShowModal(false);
      resetForm();
      loadCoupons();
    } catch (error) {
      console.error('Error saving coupon:', error);
      toast.error(error.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon deleted');
      loadCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast.error('Failed to delete coupon');
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      type: coupon.type,
      value: coupon.value,
      min_order_amount: coupon.min_order_amount || 0,
      max_discount_amount: coupon.max_discount_amount || '',
      usage_limit: coupon.usage_limit || '',
      per_user_limit: coupon.per_user_limit || 1,
      valid_from: coupon.valid_from ? new Date(coupon.valid_from).toISOString().slice(0, 16) : '',
      valid_until: coupon.valid_until ? new Date(coupon.valid_until).toISOString().slice(0, 16) : '',
      applies_to: coupon.applies_to || 'all',
      category_ids: coupon.category_ids || [],
      product_ids: coupon.product_ids || [],
      is_active: coupon.is_active,
      description: coupon.description || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      type: 'percentage',
      value: 10,
      min_order_amount: 0,
      max_discount_amount: '',
      usage_limit: '',
      per_user_limit: 1,
      valid_from: '',
      valid_until: '',
      applies_to: 'all',
      category_ids: [],
      product_ids: [],
      is_active: true,
      description: ''
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied!');
  };

  const getCouponStatus = (coupon) => {
    const now = new Date();
    const validFrom = new Date(coupon.valid_from);
    const validUntil = new Date(coupon.valid_until);

    if (!coupon.is_active) return { label: 'Inactive', color: 'bg-gray-100 text-gray-700' };
    if (now < validFrom) return { label: 'Upcoming', color: 'bg-blue-100 text-blue-700' };
    if (now > validUntil) return { label: 'Expired', color: 'bg-red-100 text-red-700' };
    return { label: 'Active', color: 'bg-green-100 text-green-700' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredCoupons = coupons.filter(c => 
    c.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-coupons">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-light text-charcoal flex items-center gap-2">
            <Ticket className="w-6 h-6 text-gold" />
            Discount Coupons
          </h1>
          <p className="text-charcoal-light mt-1">Create and manage discount codes</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-gold text-white hover:bg-gold-dark transition-colors"
          data-testid="create-coupon-btn"
        >
          <Plus className="w-4 h-4" /> Create Coupon
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-light" />
          <input
            type="text"
            placeholder="Search coupons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="expired">Expired</option>
          <option value="upcoming">Upcoming</option>
        </select>
      </div>

      {/* Coupons List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredCoupons.length === 0 ? (
        <div className="bg-white border border-charcoal/10 p-12 text-center">
          <Ticket className="w-12 h-12 mx-auto text-charcoal-light mb-3" />
          <p className="text-charcoal-light">No coupons found</p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="mt-4 text-gold hover:underline"
          >
            Create your first coupon
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredCoupons.map((coupon) => {
            const status = getCouponStatus(coupon);
            const TypeIcon = COUPON_TYPES.find(t => t.value === coupon.type)?.icon || Tag;
            
            return (
              <div 
                key={coupon.id}
                className="bg-white border border-charcoal/10 p-4 hover:border-gold/30 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                      <TypeIcon className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-lg font-medium text-charcoal">{coupon.code}</span>
                        <button
                          onClick={() => copyCode(coupon.code)}
                          className="p-1 hover:bg-beige rounded"
                          title="Copy code"
                        >
                          <Copy className="w-4 h-4 text-charcoal-light" />
                        </button>
                        <span className={`px-2 py-0.5 text-xs ${status.color}`}>{status.label}</span>
                      </div>
                      <p className="text-sm text-charcoal">
                        {coupon.type === 'percentage' && `${coupon.value}% off`}
                        {coupon.type === 'flat' && `₹${coupon.value} off`}
                        {coupon.type === 'free_shipping' && 'Free Shipping'}
                        {coupon.min_order_amount > 0 && ` on orders above ₹${coupon.min_order_amount}`}
                        {coupon.max_discount_amount && ` (max ₹${coupon.max_discount_amount})`}
                      </p>
                      <p className="text-xs text-charcoal-light mt-1">
                        {formatDate(coupon.valid_from)} - {formatDate(coupon.valid_until)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-lg font-heading text-gold">{coupon.used_count || 0}</p>
                      <p className="text-xs text-charcoal-light">
                        {coupon.usage_limit ? `/ ${coupon.usage_limit}` : 'times used'}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(coupon)}
                        className="p-2 hover:bg-beige transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4 text-charcoal-light" />
                      </button>
                      <button
                        onClick={() => handleDelete(coupon.id)}
                        className="p-2 hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-ivory w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-charcoal/10">
              <h2 className="text-xl font-heading text-charcoal">
                {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Coupon Code */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Coupon Code *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="SUMMER25"
                    className="flex-1 px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none font-mono"
                    required
                  />
                  <button
                    type="button"
                    onClick={generateCode}
                    className="px-4 py-2 border border-charcoal/20 text-charcoal hover:bg-beige"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Discount Type & Value */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Discount Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  >
                    {COUPON_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    {formData.type === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}
                  </label>
                  <input
                    type="number"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) })}
                    min="0"
                    max={formData.type === 'percentage' ? 100 : undefined}
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    disabled={formData.type === 'free_shipping'}
                  />
                </div>
              </div>

              {/* Min Order & Max Discount */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Minimum Order Amount (₹)</label>
                  <input
                    type="number"
                    value={formData.min_order_amount}
                    onChange={(e) => setFormData({ ...formData, min_order_amount: parseFloat(e.target.value) || 0 })}
                    min="0"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Max Discount (₹) - Optional</label>
                  <input
                    type="number"
                    value={formData.max_discount_amount}
                    onChange={(e) => setFormData({ ...formData, max_discount_amount: e.target.value })}
                    min="0"
                    placeholder="No limit"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              {/* Usage Limits */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Total Usage Limit - Optional</label>
                  <input
                    type="number"
                    value={formData.usage_limit}
                    onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                    min="1"
                    placeholder="Unlimited"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Per User Limit</label>
                  <input
                    type="number"
                    value={formData.per_user_limit}
                    onChange={(e) => setFormData({ ...formData, per_user_limit: parseInt(e.target.value) || 1 })}
                    min="1"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              {/* Validity Period */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Valid From *</label>
                  <input
                    type="datetime-local"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Valid Until *</label>
                  <input
                    type="datetime-local"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* Applies To */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Applies To</label>
                <select
                  value={formData.applies_to}
                  onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })}
                  className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                >
                  <option value="all">All Products</option>
                  <option value="categories">Specific Categories</option>
                  <option value="products">Specific Products</option>
                </select>
              </div>

              {/* Category/Product Selection */}
              {formData.applies_to === 'categories' && (
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Select Categories</label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-charcoal/10 p-3">
                    {categories.map((cat) => (
                      <label key={cat.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.category_ids.includes(cat.id)}
                          onChange={(e) => {
                            const ids = e.target.checked
                              ? [...formData.category_ids, cat.id]
                              : formData.category_ids.filter(id => id !== cat.id);
                            setFormData({ ...formData, category_ids: ids });
                          }}
                          className="rounded"
                        />
                        {cat.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {formData.applies_to === 'products' && (
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Select Products</label>
                  <div className="grid gap-2 max-h-40 overflow-y-auto border border-charcoal/10 p-3">
                    {products.slice(0, 20).map((prod) => (
                      <label key={prod.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.product_ids.includes(prod.id)}
                          onChange={(e) => {
                            const ids = e.target.checked
                              ? [...formData.product_ids, prod.id]
                              : formData.product_ids.filter(id => id !== prod.id);
                            setFormData({ ...formData, product_ids: ids });
                          }}
                          className="rounded"
                        />
                        {prod.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Description (Internal Note)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Summer sale promotional coupon..."
                  className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                />
              </div>

              {/* Active Status */}
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 rounded border-charcoal/20 text-gold focus:ring-gold"
                />
                <span className="text-sm text-charcoal">Active (coupon can be used)</span>
              </label>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-charcoal/10">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-charcoal/20 text-charcoal hover:bg-beige"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gold text-white hover:bg-gold-dark"
                >
                  {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
